/**
 * Core domain types.
 *
 * The engine is deliberately free of DOM, network and timer APIs so that the
 * identical code can run in the browser (optimistic local prediction) and in
 * the Durable Object (authoritative resolution). Every state transition is a
 * pure function of (state, action, now).
 */

/** A letter A-Z. Blanks are represented separately, see {@link Tile}. */
export type Letter =
  | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'M'
  | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'U' | 'V' | 'W' | 'X' | 'Y' | 'Z';

/**
 * A physical tile. `letter` is the face value; for a blank (`isBlank`) the face
 * is null until the owner assigns one, after which `assigned` carries the
 * letter it currently stands for. Assignment is reversible while the tile is on
 * the owner's own grid.
 */
export interface Tile {
  /** Stable id, unique within a match. Survives moves between rack and grid. */
  readonly id: string;
  readonly letter: Letter | null;
  readonly isBlank: boolean;
  /** The letter a blank currently represents. Always null for non-blanks. */
  assigned: Letter | null;
  /** Standard English face value for this letter; blanks are 0. */
  readonly value: number;
}

/** Integer grid coordinate. The grid is unbounded and sparse. */
export interface Coord { readonly x: number; readonly y: number; }

/** `"x,y"` — the canonical map key for a {@link Coord}. */
export type CoordKey = string;

export const key = (x: number, y: number): CoordKey => `${x},${y}`;
export const keyOf = (c: Coord): CoordKey => key(c.x, c.y);
export const parseKey = (k: CoordKey): Coord => {
  const i = k.indexOf(',');
  return { x: Number(k.slice(0, i)), y: Number(k.slice(i + 1)) };
};

export type Orientation = 'across' | 'down';

/** A maximal run of >= 2 adjacent tiles in one orientation. */
export interface Run {
  readonly orientation: Orientation;
  readonly start: Coord;
  readonly cells: readonly Coord[];
  /** Resolved letters (blanks resolved through `assigned`); '?' for unassigned. */
  readonly word: string;
}

export type RunStatus = 'valid' | 'invalid' | 'unknown' | 'incomplete';

export interface RunReport extends Run {
  readonly status: RunStatus;
}

/** Why a grid is not yet a legal finished layout. */
export type GridProblem =
  | { kind: 'empty' }
  | { kind: 'disconnected'; components: number; strayCells: readonly Coord[] }
  | { kind: 'loneTile'; cells: readonly Coord[] }
  | { kind: 'unassignedBlank'; cells: readonly Coord[] }
  | { kind: 'blankNoFit'; cells: readonly Coord[] }
  | { kind: 'invalidWord'; run: Run }
  | { kind: 'unknownWord'; run: Run };

export interface GridReport {
  readonly runs: readonly RunReport[];
  readonly problems: readonly GridProblem[];
  /**
   * What each unassigned blank was worked out to stand for.
   *
   * A blank is allowed to stay a blank: the engine finds a letter that makes
   * every word through it real, and only says the grid is wrong when there is
   * no such letter. Empty when there were no blanks to solve, or when the
   * report was asked not to consult the word list.
   */
  readonly inferred: ReadonlyMap<CoordKey, Letter>;
  /** True when the grid is a single connected component of all-valid words. */
  readonly complete: boolean;
  /** Sum of tile face values across the grid. Diagnostic/tiebreak only. */
  readonly score: number;
  readonly tileCount: number;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export type WordListId = 'enable' | 'wordnik' | 'wiktionary' | 'custom';

/**
 * Which spellings count: British (colour, centre, realise), American (color,
 * center, realize), or either. Applied on top of the word list — see
 * `SpellingDictionary`.
 */
export type SpellingMode = 'both' | 'british' | 'american';

/** How much validity feedback a player may obtain before committing. */
export type PeekMode = 'off' | 'limited' | 'live';

export type InvalidCallPenalty = 'none' | 'cooldown' | 'drawTiles';

export interface PeekPolicy {
  mode: PeekMode;
  /** Checks granted at the start of the match (`limited` mode). */
  budget: number;
  /** Extra checks granted to every seat on each successful declaration. */
  budgetPerRound: number;
  /**
   * A check is only offered while at least one placed tile is younger than
   * this many ms. 0 disables the window (checks always available).
   */
  windowMs: number;
  /** Whether a spent check also shows a definition for each word. */
  showDefinition: boolean;
  penalty: InvalidCallPenalty;
  cooldownMs: number;
  drawPenalty: number;
}

/** What opponents may observe during play. */
export interface VisibilityPolicy {
  /** Live view of other seats' grids. */
  grids: 'always' | 'onRequest' | 'afterFinish' | 'never';
  /** Live count of tiles left in other seats' racks. */
  rackCounts: boolean;
  /** The actual letters in other seats' racks. */
  rackLetters: boolean;
  /** Whether a seat's grid is revealed once they finish. */
  revealOnFinish: boolean;
}

export interface MatchConfig {
  /** Tiles dealt to each seat at the start. */
  startingTiles: number;
  /**
   * Guaranteed number of draw rounds. The bag is sized so that every seat
   * count yields exactly this many, which is what keeps a 20-seat match the
   * same length as a 4-seat one.
   */
  rounds: number;
  /**
   * Tiles every seat draws when someone declares. This is also the number in
   * the call itself: at 3 the table shouts "TAKE 3".
   */
  drawPerRound: number;
  wordList: WordListId;
  /** British spellings, American, or both. */
  spelling: SpellingMode;
  /** Minimum accepted word length. Standard play is 2. */
  minWordLength: number;
  peek: PeekPolicy;
  visibility: VisibilityPolicy;
  /** Trading one tile back for `dumpRatio` new ones. */
  dumpEnabled: boolean;
  dumpRatio: number;
  /**
   * Awarded to whoever uses every letter and brings the match to an end.
   *
   * The reward for finishing rather than merely accumulating: a dense grid
   * scores well, but going out is what stops everybody else scoring at all.
   */
  goingOutBonus: number;
  /** Show each player their own running score during the match. */
  showLiveScore: boolean;
  /**
   * Seconds of countdown between the host pressing start and anybody being
   * able to play, at a table of two or more; 0 = start at once. The letters
   * are dealt at the press but no move is accepted until the countdown ends,
   * so everyone starts at the same moment whatever their connection.
   */
  countdownSec: number;
  /** Hard time limit for the whole match in ms; 0 = untimed. */
  timeLimitMs: number;
  /**
   * Force a draw for everyone this long after the last one; 0 = off.
   *
   * Set before the match starts. It stops a table stalling on one slow player
   * without anybody having to admit defeat, and it gives a solo game a pulse.
   */
  forceDrawMs: number;
  /** Seed for the shuffle. Fixed seeds make a match exactly reproducible. */
  seed: string;
}

export const DEFAULT_PEEK: PeekPolicy = {
  mode: 'limited',
  budget: 3,
  budgetPerRound: 1,
  windowMs: 20_000,
  showDefinition: true,
  penalty: 'cooldown',
  cooldownMs: 15_000,
  drawPenalty: 1,
};

export const DEFAULT_VISIBILITY: VisibilityPolicy = {
  /*
   * Not during the match unless the host allows it: it is a race, and a
   * player's half-built grid is their own business until it is over. Every
   * grid is shown once the match ends (`revealOnFinish`).
   */
  grids: 'never',
  rackCounts: true,
  rackLetters: false,
  revealOnFinish: true,
};

export const DEFAULT_CONFIG: MatchConfig = {
  startingTiles: 7,
  rounds: 9,
  drawPerRound: 2,
  wordList: 'enable',
  spelling: 'both',
  minWordLength: 2,
  peek: DEFAULT_PEEK,
  visibility: DEFAULT_VISIBILITY,
  /*
   * Off unless the host turns it on. Trading one letter for three hands a
   * player extra letters, and extra letters make it easier to score by
   * spreading words out, instead of the harder and more rewarding craft of
   * making each letter serve twice — across and down. A tester argued for
   * it being a house rule you opt into, not the standard game.
   */
  dumpEnabled: false,
  dumpRatio: 3,
  goingOutBonus: 10,
  countdownSec: 5,
  showLiveScore: false,
  timeLimitMs: 0,
  forceDrawMs: 0,
  /**
   * Overridden with a random seed by `createMatch` unless a caller supplies
   * one. A literal default here would deal every table the same bag.
   */
  seed: '',
};

// ---------------------------------------------------------------------------
// Seats, players, teams
// ---------------------------------------------------------------------------

/**
 * A seat is one rack and one grid. A solo player occupies a seat alone; a
 * co-operating team shares one. Seats — not people — are what the bag is
 * divided between, so a team of two children competes on equal tiles with a
 * single adult and simply has two brains on the same problem.
 */
export interface Seat {
  readonly id: string;
  name: string;
  colour: string;
  /** Player ids currently occupying this seat. */
  members: string[];
  rack: Tile[];
  grid: Map<CoordKey, Tile>;
  peeksRemaining: number;
  /** Epoch ms until which declarations are refused. */
  lockedUntil: number;
  /** Epoch ms of the most recent grid mutation; drives the peek window. */
  lastPlacementAt: number;
  finishedAt: number | null;
  connectedMembers: number;
  /**
   * This seat has said it is stuck. When every active seat says so, the whole
   * table draws — the escape hatch for a position nobody can play out of.
   */
  stuck: boolean;
}

export interface Player {
  readonly id: string;
  name: string;
  seatId: string;
  isHost: boolean;
  connected: boolean;
  /**
   * When the connection dropped, if it has. A dropped connection is not a
   * departure: a phone switched away to share the invite loses its socket
   * within seconds, and treating that as leaving handed the table to whoever
   * arrived next.
   */
  disconnectedAt?: number | null;
  /** Spectators hold no seat and may never mutate a grid. */
  isSpectator: boolean;
}

export type MatchPhase = 'lobby' | 'countdown' | 'playing' | 'finished' | 'abandoned';

/**
 * Running totals for everybody who has played at this table, across every
 * match it has hosted. Keyed by player, not seat: a seat is remade when people
 * team up differently, and it is the people who are keeping score. A team's
 * score counts for each of its members.
 */
export interface SeriesTally {
  games: number;
  totals: Record<string, { name: string; points: number; wins: number; games: number }>;
}

export interface MatchState {
  readonly matchId: string;
  /** Totals across every match played at this table (see {@link SeriesTally}). */
  series?: SeriesTally;
  phase: MatchPhase;
  config: MatchConfig;
  seats: Map<string, Seat>;
  players: Map<string, Player>;
  /** Face-down tiles still to be drawn. */
  bag: Tile[];
  /** Tiles trimmed off to keep the per-seat division exact. Never dealt. */
  leftover: Tile[];
  roundsTaken: number;
  startedAt: number | null;
  /** Epoch ms of the last draw of any kind; drives the forced-draw timer. */
  lastDrawAt: number;
  finishedAt: number | null;
  winnerSeatId: string | null;
  /**
   * The seat that used every letter and ended the match, if any.
   *
   * Not the same as the winner. Going out earns a bonus and the highest score
   * wins, so a seat can go out and still lose — and a results screen that
   * assumed the winner was the one who went out dropped that seat's bonus.
   */
  wentOutSeatId: string | null;
  /** Append-only public log; the client renders this as the activity feed. */
  log: LogEntry[];
  /** Monotonic counter bumped on every applied action; used for reconciliation. */
  version: number;
  /**
   * Monotonic seat counter. Seat ids must never be reused: deriving one from
   * the current seat count reissues an id as soon as any seat is removed,
   * which silently overwrites whoever already held it.
   */
  seatSeq: number;
}

export interface LogEntry {
  readonly at: number;
  readonly kind:
    | 'joined' | 'left' | 'seated' | 'started' | 'declare' | 'dump' | 'stuck'
    | 'peek' | 'invalidCall' | 'finished' | 'stalemate' | 'config';
  readonly seatId?: string;
  readonly playerId?: string;
  readonly text: string;
}
